import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const KINDS = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'SELECT', label: 'Pick from a list' },
  { value: 'BOOLEAN', label: 'Yes / No' },
  { value: 'DATE', label: 'Date' }
];
const kindLabel = (k) => (KINDS.find((x) => x.value === k) || { label: k }).label;
const apiError = (error, fallback) => {
  const e = error.response?.data?.error || error.response?.data;
  return typeof e === 'string' && e ? e : fallback;
};

/**
 * The specification of an item a category defines (Thickness, Grade...), as boxes to fill in: one per attribute, in the kind
 * the attribute says. For a category the organization owns, "Manage attributes" opens the list to add to or change. Values are
 * { code: value }, checked again by the server against the category.
 */
export function AttributeFields({ itemTypeId, ownedByOrg, values, onChange }) {
  const [definitions, setDefinitions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [managing, setManaging] = useState(false);

  const load = useCallback(() => {
    if (!itemTypeId) {
      setDefinitions([]);
      return;
    }
    axios.get(`/api/organization-item-types/${itemTypeId}/attributes`)
      .then((r) => setDefinitions((r.data || []).filter((d) => d.isActive !== false)))
      .catch(() => setDefinitions([]))
      .finally(() => setLoaded(true));
  }, [itemTypeId]);

  useEffect(() => { setLoaded(false); load(); }, [load]);

  if (!itemTypeId || (definitions.length === 0 && !ownedByOrg)) return null;

  const set = (code, value) => onChange({ ...(values || {}), [code]: value });

  return (
    <div className="border rounded p-2 mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong className="small">Specification</strong>
        {ownedByOrg && (
          <Button variant="link" size="sm" className="p-0" onClick={() => setManaging(true)}>
            Manage attributes
          </Button>
        )}
      </div>
      {loaded && definitions.length === 0 && (
        <div className="small text-muted">
          Nothing yet. Add what describes this material, like Thickness or Grade, with Manage attributes.
        </div>
      )}
      {definitions.map((d) => {
        const value = values ? values[d.code] : undefined;
        const label = d.unit ? `${d.name} (${d.unit})` : d.name;
        if (d.dataType === 'BOOLEAN') {
          return (
            <Form.Check
              key={d.code}
              type="checkbox"
              id={`attr-${d.code}`}
              className="mb-2"
              label={label}
              checked={value === true}
              onChange={(e) => set(d.code, e.target.checked)}
            />
          );
        }
        return (
          <Form.Group className="mb-2" key={d.code} controlId={`attr-${d.code}`}>
            <Form.Label className="small mb-1">{label}</Form.Label>
            {d.dataType === 'SELECT' ? (
              <Form.Select size="sm" value={value ?? ''} onChange={(e) => set(d.code, e.target.value)}>
                <option value="">Not set</option>
                {(d.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </Form.Select>
            ) : (
              <Form.Control
                size="sm"
                type={d.dataType === 'NUMBER' ? 'number' : d.dataType === 'DATE' ? 'date' : 'text'}
                step={d.dataType === 'NUMBER' ? 'any' : undefined}
                maxLength={d.dataType === 'TEXT' ? 200 : undefined}
                value={value ?? ''}
                onChange={(e) => set(d.code, e.target.value)}
              />
            )}
          </Form.Group>
        );
      })}
      {managing && (
        <AttributeManager typeId={itemTypeId} onHide={() => setManaging(false)} onChanged={load} />
      )}
    </div>
  );
}

const emptyForm = { name: '', dataType: 'SELECT', unit: '', optionsText: '' };

/** The attributes of a category the organization owns: add, change, switch off, delete (not once items use one). */
export function AttributeManager({ typeId, onHide, onChanged }) {
  const [definitions, setDefinitions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`/api/organization-item-types/${typeId}/attributes`)
      .then((r) => setDefinitions(r.data || []))
      .catch((e) => toast.error(apiError(e, 'Could not load the attributes')));
  }, [typeId]);

  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); if (onChanged) onChanged(); };

  const payload = () => ({
    name: form.name,
    dataType: form.dataType,
    unit: form.unit || null,
    options: form.dataType === 'SELECT' ? form.optionsText.split('\n') : null
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/organization-item-types/attributes/${editing.id}`, { ...payload(), isActive: editing.isActive });
        toast.success('Attribute updated');
      } else {
        await axios.post(`/api/organization-item-types/${typeId}/attributes`, payload());
        toast.success('Attribute added');
      }
      setForm(emptyForm);
      setEditing(null);
      changed();
    } catch (error) {
      toast.error(apiError(error, 'Could not save the attribute'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (d) => {
    setEditing(d);
    setForm({ name: d.name, dataType: d.dataType, unit: d.unit || '', optionsText: (d.options || []).join('\n') });
  };

  const toggleActive = async (d) => {
    try {
      await axios.put(`/api/organization-item-types/attributes/${d.id}`, {
        name: d.name, dataType: d.dataType, unit: d.unit, options: d.options, isActive: !d.isActive
      });
      changed();
    } catch (error) {
      toast.error(apiError(error, 'Could not change the attribute'));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete the attribute "${d.name}"?`)) return;
    try {
      await axios.delete(`/api/organization-item-types/attributes/${d.id}`);
      toast.success('Attribute deleted');
      changed();
    } catch (error) {
      toast.error(apiError(error, 'Could not delete the attribute'));
    }
  };

  return (
    <Modal show onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Attributes of this category</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          The things that tell one item of this category from another, such as Thickness or Grade. They are filled in on each item.
          Once an item has a value, the attribute&apos;s kind stays as it is and a list option in use cannot be removed; you can still
          rename it, add options, or switch it off.
        </p>
        <Table size="sm" bordered responsive>
          <thead><tr><th>Attribute</th><th>Kind</th><th>Options</th><th /></tr></thead>
          <tbody>
            {definitions.length === 0 && (
              <tr><td colSpan={4} className="text-center text-muted">No attributes yet</td></tr>
            )}
            {definitions.map((d) => (
              <tr key={d.id}>
                <td>
                  {d.name}{d.unit ? ` (${d.unit})` : ''}
                  {d.isActive === false && <Badge bg="secondary" className="ms-1">Off</Badge>}
                </td>
                <td>{kindLabel(d.dataType)}</td>
                <td className="small">{(d.options || []).join(', ') || '-'}</td>
                <td className="text-nowrap">
                  <Button size="sm" variant="outline-primary" className="me-1" onClick={() => startEdit(d)}>Edit</Button>
                  <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => toggleActive(d)}>
                    {d.isActive === false ? 'Switch on' : 'Switch off'}
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => remove(d)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h6>{editing ? `Change "${editing.name}"` : 'Add an attribute'}</h6>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-1">Name</Form.Label>
          <Form.Control size="sm" value={form.name} placeholder="Thickness" onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Form.Group>
        <div className="d-flex gap-2">
          <Form.Group className="mb-2 flex-fill">
            <Form.Label className="small mb-1">Kind</Form.Label>
            <Form.Select size="sm" value={form.dataType} onChange={(e) => setForm({ ...form, dataType: e.target.value })}>
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2 flex-fill">
            <Form.Label className="small mb-1">Unit (optional)</Form.Label>
            <Form.Control size="sm" value={form.unit} placeholder="mm" maxLength={20} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          </Form.Group>
        </div>
        {form.dataType === 'SELECT' && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Options, one on each line</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              size="sm"
              value={form.optionsText}
              placeholder={'18 gauge\n20 gauge\n1.2 mm'}
              onChange={(e) => setForm({ ...form, optionsText: e.target.value })}
            />
          </Form.Group>
        )}
        <div className="d-flex gap-2">
          <Button size="sm" onClick={save} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add attribute'}
          </Button>
          {editing && (
            <Button size="sm" variant="outline-secondary" onClick={() => { setEditing(null); setForm(emptyForm); }}>
              Cancel
            </Button>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AttributeFields;
